import cloneDeep from 'lodash-es/cloneDeep'
import { reverse } from 'named-urls'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'

import { Toasts } from '@fourthwall/components'
import { routing } from '@utils/routing'

import { useCancellationOffers } from '../../hooks/useCancellationOffers'
import { useOrderIdParam } from '../../hooks/useOrderIdParam'
import { useOrderGroupedByShipmentsQuery } from '../../queries/useOrderGroupedByShipmentsQuery'
import type { EditableOffer } from './OrderEdit.types'
import { OrderEditView } from './OrderEditView'
import { useCalculateItemsReplacementsQuery } from './queries/useCalculateItemsReplacementsQuery'
import { useItemsReplacementMutation } from './queries/useItemsReplacementMutation'
import { flattenOffers } from './utils/flattenOffers'
import { getReplacementsPayload } from './utils/getReplacementsPayload'

export const OrderEditContainer = () => {
  const navigate = useNavigate()
  const { orderId } = useOrderIdParam()
  const { orderGroupedByShipmentsQuery } = useOrderGroupedByShipmentsQuery([{ orderId }])

  const [editableOffers, setEditableOffers] = useState<EditableOffer[]>()
  const [shouldRestock, setShouldRestock] = useState(true)
  const replacements = getReplacementsPayload(editableOffers)
  const {
    cancellableOffers,
    nonCancellableOffers,
    partiallyCancellable,
    orderCancellationQuery,
    invalidateOrderCancellationQuery,
  } = useCancellationOffers(orderId)
  const { calculateItemsReplacementsQuery } = useCalculateItemsReplacementsQuery(
    [
      { orderId },
      {
        replacements,
        restockReplacedItems: shouldRestock,
        skipStatusVerification: false,
        skipThirdPartyOrdersSync: false,
      },
    ],
    { retry: false, keepPreviousData: true },
  )
  const { itemsReplacementMutation } = useItemsReplacementMutation(orderId)
  const calculateItemsReplacements = calculateItemsReplacementsQuery.data

  useEffect(() => {
    if (orderCancellationQuery.isSuccess && cancellableOffers.length) {
      setEditableOffers(
        flattenOffers(cancellableOffers).map((offer) => ({
          id: uuidv4(),
          original: cloneDeep(offer),
          current: cloneDeep(offer),
        })),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderCancellationQuery.isSuccess])

  const handleVariantEdit = (editableOffer: EditableOffer) => {
    setEditableOffers((currentEditableOffers) =>
      currentEditableOffers?.map((currentEditableOffer) => {
        if (editableOffer.id === currentEditableOffer.id) {
          return editableOffer
        }

        return currentEditableOffer
      }),
    )
  }

  const handleOrderEdit = (reason?: string) => {
    itemsReplacementMutation.mutate(
      {
        replacements: getReplacementsPayload(editableOffers),
        restockReplacedItems: shouldRestock,
        reason,
        skipStatusVerification: false,
        skipThirdPartyOrdersSync: false,
      },
      {
        onSuccess: () => {
          invalidateOrderCancellationQuery()
          navigate(reverse(routing.contributions.orders.order.self, { orderId }))
          Toasts.notify('Order has been updated successfully.', { type: 'info' })
        },
      },
    )
  }

  return (
    <OrderEditView
      orderId={orderId}
      friendlyId={orderGroupedByShipmentsQuery.data?.friendlyId}
      editableOffers={editableOffers}
      nonEditableOffers={nonCancellableOffers}
      replacements={replacements}
      donation={orderGroupedByShipmentsQuery.data?.donation || undefined}
      promotion={orderGroupedByShipmentsQuery.data?.promotion}
      subtotal={calculateItemsReplacements?.afterReplacement.items}
      shipping={calculateItemsReplacements?.afterReplacement.shipping}
      taxes={calculateItemsReplacements?.afterReplacement.tax}
      discount={calculateItemsReplacements?.afterReplacement.discount}
      total={calculateItemsReplacements?.afterReplacement.total}
      paidTotal={calculateItemsReplacements?.beforeReplacement.total}
      shouldRestock={shouldRestock}
      isEditButtonDisabled={
        !partiallyCancellable ||
        !editableOffers?.length ||
        calculateItemsReplacementsQuery.isFetching ||
        orderCancellationQuery.isFetching ||
        calculateItemsReplacementsQuery.isError ||
        orderCancellationQuery.isError ||
        itemsReplacementMutation.isError
      }
      isLoading={
        orderGroupedByShipmentsQuery.isLoading ||
        calculateItemsReplacementsQuery.isLoading ||
        orderCancellationQuery.isLoading ||
        itemsReplacementMutation.isLoading
      }
      onVariantEdit={handleVariantEdit}
      onOrderEdit={handleOrderEdit}
      onRestockChange={setShouldRestock}
    />
  )
}
